import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  PlayArrow,
  Timer,
  CheckCircle,
  School,
  EmojiEvents,
  Assessment,
  ArrowForward,
  ArrowBack,
  Close,
  Warning,
} from '@mui/icons-material';
import AnswerSheetDock from '../components/AnswerSheetDock';

const API_URL = process.env.REACT_APP_API_URL;

// Configuraciones de simulacro
const SIMULACRO_CONFIGS = {
  rapido: {
    name: 'Rápido',
    multiplier: 0.2, // 20% de preguntas
    description: '25 preguntas • ~50 minutos',
  },
  medio: {
    name: 'Medio',
    multiplier: 0.5, // 50% de preguntas
    description: '63 preguntas • ~2 horas',
  },
  completo: {
    name: 'Completo',
    multiplier: 1.0, // 100% de preguntas
    description: '126 preguntas • ~4 horas 30 min',
  },
};

const BASE_AREAS = [
  { id: 'matematicas', name: 'Matemáticas', questions: 25, time: 45, color: '#e91e63' },
  { id: 'lectura-critica', name: 'Lectura Crítica', questions: 20, time: 40, color: '#9c27b0' },
  { id: 'sociales', name: 'Sociales y Ciudadanas', questions: 25, time: 40, color: '#3f51b5' },
  { id: 'ciencias', name: 'Ciencias Naturales', questions: 29, time: 45, color: '#009688' },
  { id: 'ingles', name: 'Inglés', questions: 27, time: 35, color: '#ff5722' },
];

const STORAGE_KEY = 'simulacro_state';

const SimulacroPage = () => {
  const navigate = useNavigate();
  const [simulacroMode, setSimulacroMode] = useState('completo');
  const [activeStep, setActiveStep] = useState(0);
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [sessionIds, setSessionIds] = useState({}); // Guardar sessionId por área
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Calcular áreas según el modo seleccionado
  const AREAS = BASE_AREAS.map(area => ({
    ...area,
    questions: Math.round(area.questions * SIMULACRO_CONFIGS[simulacroMode].multiplier),
    time: Math.round(area.time * SIMULACRO_CONFIGS[simulacroMode].multiplier),
  }));

  const TOTAL_QUESTIONS = AREAS.reduce((sum, area) => sum + area.questions, 0);
  const TOTAL_TIME = AREAS.reduce((sum, area) => sum + area.time, 0) * 60; // En segundos

  // Inicializar timeLeft cuando cambia el modo de simulacro
  useEffect(() => {
    if (!started) {
      setTimeLeft(TOTAL_TIME);
    }
  }, [simulacroMode, started, TOTAL_TIME]);

  // Guardar estado en localStorage cada vez que cambie algo importante
  useEffect(() => {
    if (started && questions.length > 0) {
      const state = {
        simulacroMode,
        questions,
        answers,
        currentQuestion,
        timeLeft,
        sessionIds,
        startedAt: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [answers, currentQuestion, timeLeft, questions, sessionIds, started, simulacroMode]);

  // Cargar estado previo al montar el componente
  useEffect(() => {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        // Verificar que no haya pasado más de 12 horas
        const elapsed = Date.now() - state.startedAt;
        if (elapsed < 12 * 60 * 60 * 1000) {
          // Preguntar al usuario si quiere continuar
          if (window.confirm('¿Deseas continuar con el simulacro que dejaste pendiente?')) {
            setSimulacroMode(state.simulacroMode || 'completo');
            setQuestions(state.questions);
            setAnswers(state.answers);
            setCurrentQuestion(state.currentQuestion);
            setTimeLeft(state.timeLeft);
            setSessionIds(state.sessionIds);
            setStarted(true);
            setShowInstructions(false);
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error('Error loading saved state:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Obtener preguntas de todas las áreas
  const fetchAllQuestions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const allQuestions = [];
      const sessions = {};
      
      for (const area of AREAS) {
        const response = await axios.get(
          `${API_URL}/api/exams/questions/${area.id}?limit=${area.questions}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        console.log(`📥 Preguntas cargadas para ${area.name}:`, {
          total: response.data.questions.length,
          sessionId: response.data.sessionId,
          sampleQuestion: response.data.questions[0] ? {
            id: response.data.questions[0].id,
            hasContext: !!(response.data.questions[0].context_text || response.data.questions[0].context_image),
            hasQuestionSet: !!response.data.questions[0].question_set_id,
            contextText: response.data.questions[0].context_text?.substring(0, 50) + '...',
            questionSetId: response.data.questions[0].question_set_id
          } : 'No questions'
        });
        
        // Guardar el sessionId de esta área
        sessions[area.id] = response.data.sessionId;
        
        // Parsear options si vienen como string
        const parsedQuestions = response.data.questions.map(q => ({
          ...q,
          options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options,
          area: area.name,
          areaId: area.id,
          areaColor: area.color,
        }));
        
        allQuestions.push(...parsedQuestions);
      }
      
      console.log(`📊 Resumen de carga completa:`, {
        totalQuestions: allQuestions.length,
        withContext: allQuestions.filter(q => q.context_text || q.context_image).length,
        withQuestionSet: allQuestions.filter(q => q.question_set_id).length,
        byArea: AREAS.map(a => ({
          name: a.name,
          expected: a.questions,
          loaded: allQuestions.filter(q => q.areaId === a.id).length
        }))
      });
      
      setSessionIds(sessions);
      setQuestions(allQuestions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', error);
      alert('Error al cargar las preguntas. Por favor, recarga la página.');
      setLoading(false);
    }
  };

  const handleStartSimulacro = async () => {
    setShowInstructions(false);
    await fetchAllQuestions();
    setStarted(true);
  };

  // Timer
  useEffect(() => {
    if (!started || showResults) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [started, showResults]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (answer) => {
    setAnswers({
      ...answers,
      [currentQuestion]: answer,
    });
  };

  const checkUnanswered = () => {
    const unansweredCount = questions.length - Object.keys(answers).length;
    return unansweredCount;
  };

  const handleFinishClick = () => {
    const unanswered = checkUnanswered();
    if (unanswered > 0) {
      setConfirmDialogOpen(true);
    } else {
      handleSubmit();
    }
  };

  const handleConfirmSubmit = () => {
    setConfirmDialogOpen(false);
    handleSubmit();
  };

  const handleSubmit = async () => {
    if (submitting) return; // Evitar múltiples envíos
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Agrupar respuestas por área MANTENIENDO los índices globales
      const areaAnswers = {};
      const areaResults = {};
      const globalIndexToLocalIndex = {}; // Mapeo de índice global a local por área
      
      questions.forEach((q, globalIndex) => {
        if (!areaAnswers[q.areaId]) {
          areaAnswers[q.areaId] = {
            answers: {},
            questions: [],
            color: q.areaColor,
            name: q.area,
          };
        }
        
        const localIndex = areaAnswers[q.areaId].questions.length;
        
        // Guardar mapeo de índice global a local
        globalIndexToLocalIndex[globalIndex] = { areaId: q.areaId, localIndex };
        
        // Usar índice global para obtener la respuesta
        if (answers[globalIndex] !== undefined) {
          areaAnswers[q.areaId].answers[localIndex] = answers[globalIndex];
        }
        
        areaAnswers[q.areaId].questions.push(q);
      });

      console.log('📊 Agrupación de respuestas por área:', {
        totalQuestions: questions.length,
        totalAnswers: Object.keys(answers).length,
        areaBreakdown: Object.entries(areaAnswers).map(([areaId, data]) => ({
          area: data.name,
          questions: data.questions.length,
          answered: Object.keys(data.answers).length
        }))
      });

      // Enviar cada área por separado
      let totalCorrect = 0;
      let totalQuestions = 0;
      
      for (const areaId of Object.keys(areaAnswers)) {
        const areaData = areaAnswers[areaId];
        const sessionId = sessionIds[areaId];
        
        if (!sessionId) {
          console.error(`No sessionId found for area: ${areaId}`);
          continue;
        }

        try {
          console.log(`📤 Enviando área ${areaId}:`, {
            area: areaData.name,
            sessionId,
            totalQuestions: areaData.questions.length,
            answersProvided: Object.keys(areaData.answers).length,
            answers: areaData.answers
          });

          const response = await axios.post(
            `${API_URL}/api/exams/submit`,
            {
              area: areaId,
              sessionId: sessionId,
              answers: areaData.answers,
              timeSpent: TOTAL_TIME - timeLeft,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          console.log(`✅ Respuesta del servidor para ${areaData.name}:`, {
            correct: response.data.correct,
            incorrect: response.data.incorrect,
            total: response.data.total,
            score: response.data.score
          });

          areaResults[areaId] = {
            name: areaData.name,
            color: areaData.color,
            correct: response.data.correct,
            total: response.data.total,
          };
          
          totalCorrect += response.data.correct;
          totalQuestions += response.data.total;
        } catch (error) {
          console.error(`❌ Error submitting area ${areaId}:`, error);
          console.error('Error details:', error.response?.data);
        }
      }

      const overallScore = Math.round((totalCorrect / totalQuestions) * 100);

      // Guardar resultado en el historial
      try {
        await axios.post(
          `${API_URL}/api/results/save`,
          {
            area: 'completo',
            score: overallScore,
            correct_answers: totalCorrect,
            incorrect_answers: totalQuestions - totalCorrect,
            time_spent: TOTAL_TIME - timeLeft,
            total_questions: totalQuestions,
            area_results: Object.values(areaResults),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Resultado guardado en historial');
      } catch (error) {
        console.error('❌ Error al guardar en historial:', error);
        // No bloqueamos si falla el guardado del historial
      }

      setResults({
        score: overallScore,
        correct: totalCorrect,
        total: totalQuestions,
        areaResults: Object.values(areaResults),
        timeSpent: TOTAL_TIME - timeLeft,
      });
      
      // Limpiar localStorage al finalizar exitosamente
      localStorage.removeItem(STORAGE_KEY);
      
      setShowResults(true);
      setSubmitting(false);
    } catch (error) {
      console.error('Error submitting results:', error);
      alert('Error al guardar los resultados. Por favor, inténtalo de nuevo.');
      setSubmitting(false);
    }
  };

  const progress = questions.length > 0 ? ((currentQuestion + 1) / questions.length) * 100 : 0;
  const question = questions[currentQuestion];
  const optionLabels = ['A', 'B', 'C', 'D'];

  // Vista de instrucciones
  if (showInstructions) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <School sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" gutterBottom fontWeight="bold">
              Simulacro Pre-ICFES Completo
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Prepárate para el examen oficial Saber 11°
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            📊 Selecciona la modalidad del simulacro:
          </Typography>
          
          <ToggleButtonGroup
            value={simulacroMode}
            exclusive
            onChange={(e, newMode) => newMode && setSimulacroMode(newMode)}
            fullWidth
            sx={{ mb: 4 }}
          >
            {Object.entries(SIMULACRO_CONFIGS).map(([key, config]) => (
              <ToggleButton 
                key={key} 
                value={key}
                sx={{ 
                  py: 2,
                  flexDirection: 'column',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {config.name}
                </Typography>
                <Typography variant="caption">
                  {config.description}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'primary.main', color: 'white', height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight="bold">
                    {TOTAL_QUESTIONS}
                  </Typography>
                  <Typography variant="body1">
                    Preguntas Totales
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'secondary.main', color: 'white', height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight="bold">
                    {Math.floor(TOTAL_TIME / 60)}
                  </Typography>
                  <Typography variant="body1">
                    Minutos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'success.main', color: 'white', height: '100%' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight="bold">
                    5
                  </Typography>
                  <Typography variant="body1">
                    Áreas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            📋 Distribución del examen:
          </Typography>
          <List>
            {AREAS.map((area) => (
              <ListItem key={area.id}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: area.color,
                    mr: 2,
                  }}
                />
                <ListItemText
                  primary={area.name}
                  secondary={`${area.questions} preguntas • ${area.time} minutos`}
                />
              </ListItem>
            ))}
          </List>

          <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2">
              <strong>Importante:</strong> Este simulacro replica las condiciones del examen oficial Saber 11°.
              Una vez iniciado, el cronómetro no se detendrá. Asegúrate de tener tiempo disponible antes de comenzar.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/dashboard')}
              startIcon={<ArrowBack />}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleStartSimulacro}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
              sx={{ px: 6 }}
            >
              {loading ? 'Cargando...' : 'Iniciar Simulacro'}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Vista de resultados
  if (showResults && results) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <EmojiEvents sx={{ fontSize: 100, color: 'warning.main', mb: 2 }} />
            <Typography variant="h3" gutterBottom fontWeight="bold">
              ¡Simulacro Completado!
            </Typography>
            <Typography variant="h5" color="text.secondary">
              Tu puntuación: {results.score}/100
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {results.correct} respuestas correctas de {results.total}
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 50, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {results.score}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Puntuación Total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Timer sx={{ fontSize: 50, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {formatTime(results.timeSpent)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tiempo Utilizado
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Assessment sx={{ fontSize: 50, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {results.correct}/{results.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Respuestas Correctas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" gutterBottom fontWeight="bold">
            Resultados por Área
          </Typography>

          <Grid container spacing={2}>
            {results.areaResults.map((area, index) => {
              const percentage = Math.round((area.correct / area.total) * 100);
              return (
                <Grid item xs={12} md={6} key={index}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: area.color,
                            mr: 2,
                          }}
                        />
                        <Typography variant="h6" fontWeight="bold">
                          {area.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: area.color,
                              },
                            }}
                          />
                        </Box>
                        <Typography variant="body1" fontWeight="bold">
                          {percentage}%
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {area.correct} de {area.total} correctas
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/ranking')}
              startIcon={<EmojiEvents />}
            >
              Ver Ranking
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/dashboard')}
              startIcon={<ArrowBack />}
            >
              Volver al Dashboard
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Vista del examen en progreso
  if (started && !loading && questions.length > 0) {
    const question = questions[currentQuestion];
    const optionLabels = ['A', 'B', 'C', 'D'];
    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: 2, pb: 8 }}>
        {/* Header con progreso */}
        <Paper 
          elevation={2} 
          sx={{ 
            position: 'sticky', 
            top: 0, 
            zIndex: 1100,
            borderRadius: 0,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ py: 2 }}>
              {/* Timer y progreso */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    SIMULACRO SABER 11°
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pregunta {currentQuestion + 1} de {questions.length}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Timer color={timeLeft < 600 ? 'error' : 'primary'} />
                    <Typography 
                      variant="h5" 
                      fontWeight="bold"
                      color={timeLeft < 600 ? 'error' : 'primary'}
                    >
                      {formatTime(timeLeft)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Tiempo restante
                  </Typography>
                </Box>
              </Box>

              {/* Barra de progreso */}
              <Box sx={{ mb: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: question?.areaColor || 'primary.main',
                    },
                  }}
                />
              </Box>

              {/* Chip del área actual */}
              <Chip
                label={question?.area || 'Cargando...'}
                sx={{
                  bgcolor: question?.areaColor || 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            </Box>
          </Container>
        </Paper>

        {/* Contenido de la pregunta */}
        <Container maxWidth="lg" sx={{ mt: 3, minHeight: '70vh' }}>
          {/* Panel de contexto (si existe) - ARRIBA */}
          {(question?.context_text || question?.context_image) && (
            <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📄 Contexto
                </Typography>
                {question?.question_set_id && (
                  <Chip 
                    label={`Set #${question.question_set_id}`} 
                    size="small" 
                    color="primary" 
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {question.context_text && (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2, lineHeight: 1.8 }}>
                  {question.context_text}
                </Typography>
              )}
              
              {question.context_image && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <img
                    src={question.context_image}
                    alt="Contexto"
                    style={{ maxWidth: '100%', borderRadius: 8 }}
                  />
                </Box>
              )}
            </Paper>
          )}

          {/* Pregunta y opciones */}
          <Paper elevation={2} sx={{ p: 4 }}>
            {/* Indicador de set de preguntas */}
            {question?.question_set_id && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  📚 Pregunta del Set #{question.question_set_id}
                </Typography>
                <Typography variant="caption">
                  Esta pregunta está basada en el contexto mostrado arriba
                </Typography>
              </Alert>
            )}

            {/* Pregunta */}
            <Typography variant="h6" gutterBottom fontWeight="bold">
              {question?.question}
            </Typography>

                {/* Opciones */}
                <Box sx={{ mt: 3 }}>
                  {question?.options?.map((option, index) => (
                    <Button
                      key={index}
                      fullWidth
                      variant={answers[currentQuestion] === optionLabels[index] ? 'contained' : 'outlined'}
                      onClick={() => handleAnswerChange(optionLabels[index])}
                      sx={{
                        mb: 2,
                        p: 2,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        textTransform: 'none',
                        bgcolor: answers[currentQuestion] === optionLabels[index] ? question.areaColor : 'transparent',
                        borderColor: answers[currentQuestion] === optionLabels[index] ? question.areaColor : 'grey.300',
                        '&:hover': {
                          bgcolor: answers[currentQuestion] === optionLabels[index] ? question.areaColor : 'grey.50',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: answers[currentQuestion] === optionLabels[index] ? 'white' : 'grey.200',
                          color: answers[currentQuestion] === optionLabels[index] ? question.areaColor : 'text.primary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        {optionLabels[index]}
                      </Box>
                      <Typography>{option}</Typography>
                    </Button>
                  ))}
                </Box>

                {/* Navegación */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                    disabled={currentQuestion === 0}
                    startIcon={<ArrowBack />}
                  >
                    Anterior
                  </Button>
                  
                  {currentQuestion === questions.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleFinishClick}
                      disabled={submitting}
                      color="success"
                      sx={{ px: 4 }}
                      startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {submitting ? 'Enviando...' : 'Finalizar Simulacro'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => setCurrentQuestion(Math.min(questions.length - 1, currentQuestion + 1))}
                      endIcon={<ArrowForward />}
                      sx={{ bgcolor: question.areaColor }}
                    >
                      Siguiente
                    </Button>
                  )}
                </Box>
              </Paper>
        </Container>

        {/* Hoja de respuestas flotante (AnswerSheetDock) */}
        <AnswerSheetDock
          totalQuestions={questions.length}
          answers={answers}
          onAnswerSelect={handleAnswerChange}
          currentQuestion={currentQuestion}
          onQuestionNavigate={setCurrentQuestion}
        />

        {/* Diálogo de confirmación */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Preguntas sin contestar
          </DialogTitle>
          <DialogContent>
            <Typography>
              Tienes <strong>{checkUnanswered()} preguntas sin contestar</strong>.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              ¿Estás seguro de que deseas finalizar el simulacro?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>
              Revisar
            </Button>
            <Button 
              onClick={handleConfirmSubmit} 
              variant="contained" 
              color="warning"
            >
              Finalizar de todos modos
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Vista de carga
  return (
    <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
      <CircularProgress size={60} />
      <Typography variant="h6" sx={{ mt: 3 }}>
        Cargando simulacro...
      </Typography>
    </Container>
  );
};

export default SimulacroPage;
